// src/geo.js
// US ZIP code geolocation utilities.
//
// On first use, fetches a compact US ZIP-to-coordinates dataset from a public
// CDN (~700 KB ungzipped), caches it in localStorage indefinitely, and exposes
// lookup + distance functions. All subsequent app loads use the cached data
// instantly with zero network requests.

const ZIP_CDN_URL = "https://cdn.jsdelivr.net/gh/millbj92/US-Zip-Codes-JSON@master/USCities.json";
const STORAGE_KEY = "crm_zip_db_v1";

// Singleton in-memory state. Built lazily on first call.
let zipByCode      = null;   // { "37027": {lat, lng, city, state}, ... }
let cityStateIndex = null;   // { "TN|NASHVILLE": {lat, lng}, ... }  -- centroid of all zips in that city
let loadPromise    = null;   // Cached promise so concurrent callers share the same fetch

// US states list for the dropdown UI
export const US_STATES = [
  ["AL","Alabama"],["AK","Alaska"],["AZ","Arizona"],["AR","Arkansas"],
  ["CA","California"],["CO","Colorado"],["CT","Connecticut"],["DE","Delaware"],
  ["FL","Florida"],["GA","Georgia"],["HI","Hawaii"],["ID","Idaho"],
  ["IL","Illinois"],["IN","Indiana"],["IA","Iowa"],["KS","Kansas"],
  ["KY","Kentucky"],["LA","Louisiana"],["ME","Maine"],["MD","Maryland"],
  ["MA","Massachusetts"],["MI","Michigan"],["MN","Minnesota"],["MS","Mississippi"],
  ["MO","Missouri"],["MT","Montana"],["NE","Nebraska"],["NV","Nevada"],
  ["NH","New Hampshire"],["NJ","New Jersey"],["NM","New Mexico"],["NY","New York"],
  ["NC","North Carolina"],["ND","North Dakota"],["OH","Ohio"],["OK","Oklahoma"],
  ["OR","Oregon"],["PA","Pennsylvania"],["RI","Rhode Island"],["SC","South Carolina"],
  ["SD","South Dakota"],["TN","Tennessee"],["TX","Texas"],["UT","Utah"],
  ["VT","Vermont"],["VA","Virginia"],["WA","Washington"],["WV","West Virginia"],
  ["WI","Wisconsin"],["WY","Wyoming"],["DC","District of Columbia"],
];

// Normalize a state input -- accepts "TN" or "Tennessee", returns "TN" (uppercase 2-letter)
// IMPORTANT: returns "" for anything that isn't a real US state. This is what
// blocks foreign addresses like "Ile-de-France" or "Ontario" from
// incorrectly matching US state codes (IL, ON->none) when truncated.
export function normalizeState(s) {
  if (!s) return "";
  const upper = String(s).trim().toUpperCase();
  // Exact 2-letter match
  if (upper.length === 2 && US_STATES.find(([code]) => code === upper)) return upper;
  // Exact full-name match
  const match = US_STATES.find(([, name]) => name.toUpperCase() === upper);
  return match ? match[0] : "";
}

// Build the city-state index from the zip dictionary. Picks the first ZIP we
// see for any (state, city) pair -- close enough for distance calculations.
function buildCityStateIndex(zips) {
  const idx = {};
  for (const z of zips) {
    const key = z.state + "|" + z.city.toUpperCase();
    if (!idx[key]) {
      idx[key] = { lat: z.lat, lng: z.lng };
    }
  }
  return idx;
}

// Load the ZIP dataset. Tries localStorage first; falls back to the CDN.
// Multiple concurrent callers share a single fetch promise.
export function loadZipDatabase() {
  if (zipByCode) return Promise.resolve(true);
  if (loadPromise) return loadPromise;

  loadPromise = (async () => {
    // 1. Try cache
    try {
      const cached = localStorage.getItem(STORAGE_KEY);
      if (cached) {
        const arr = JSON.parse(cached);
        if (Array.isArray(arr) && arr.length > 30000) {
          zipByCode = {};
          for (const z of arr) zipByCode[z.zip] = z;
          cityStateIndex = buildCityStateIndex(arr);
          console.log("[geo] Loaded " + arr.length + " ZIPs from cache");
          return true;
        }
      }
    } catch (e) {
      console.warn("[geo] Cache read failed:", e.message);
    }

    // 2. Fetch from CDN
    try {
      console.log("[geo] Fetching ZIP database from CDN (one-time, ~700KB)...");
      const res = await fetch(ZIP_CDN_URL);
      if (!res.ok) throw new Error("CDN returned " + res.status);
      const raw = await res.json();
      // Source format: [{zip_code, latitude, longitude, city, state}, ...]
      // Normalize to our compact shape
      const arr = raw.map(r => ({
        zip:   String(r.zip_code || r.zip || "").padStart(5, "0"),
        lat:   parseFloat(r.latitude  ?? r.lat),
        lng:   parseFloat(r.longitude ?? r.lng),
        city:  String(r.city || ""),
        state: String(r.state || "").toUpperCase(),
      })).filter(z => z.zip && !isNaN(z.lat) && !isNaN(z.lng));

      // Save to cache
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(arr));
      } catch (e) {
        console.warn("[geo] Cache write failed (likely quota):", e.message);
      }

      zipByCode = {};
      for (const z of arr) zipByCode[z.zip] = z;
      cityStateIndex = buildCityStateIndex(arr);
      console.log("[geo] Loaded " + arr.length + " ZIPs from CDN");
      return true;
    } catch (err) {
      console.error("[geo] Failed to load ZIP database:", err.message);
      zipByCode = {};
      cityStateIndex = {};
      return false;
    }
  })();

  return loadPromise;
}

// Look up lat/lng for a ZIP. Returns null if unknown or DB not yet loaded.
// Accepts 5-digit ZIPs; ZIP+4 ("37027-1234") is truncated to 5.
export function latLngForZip(zip) {
  if (!zipByCode || !zip) return null;
  const z = String(zip).replace(/[^\d]/g, "").slice(0, 5).padStart(5, "0");
  const hit = zipByCode[z];
  return hit ? { lat: hit.lat, lng: hit.lng } : null;
}

// Look up lat/lng for a (city, state) pair. Returns null if unknown.
// State should be 2-letter uppercase; case-insensitive matching on city.
export function latLngForCityState(city, state) {
  if (!cityStateIndex || !city || !state) return null;
  const key = normalizeState(state) + "|" + String(city).trim().toUpperCase();
  const hit = cityStateIndex[key];
  return hit ? { lat: hit.lat, lng: hit.lng } : null;
}

// Haversine distance in miles between two {lat, lng} points.
const EARTH_RADIUS_MI = 3958.8;
export function milesBetween(a, b) {
  if (!a || !b) return Infinity;
  const toRad = d => d * Math.PI / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const sa = Math.sin(dLat/2) * Math.sin(dLat/2) +
             Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) *
             Math.sin(dLng/2) * Math.sin(dLng/2);
  const c = 2 * Math.atan2(Math.sqrt(sa), Math.sqrt(1 - sa));
  return EARTH_RADIUS_MI * c;
}

// Resolve a destination spec to lat/lng. Priority: ZIP > city+state > state-only.
// `state` can be 2-letter or full name.
export function resolveDestination({zip, city, state}) {
  if (zip) {
    const hit = latLngForZip(zip);
    if (hit) return { ...hit, resolution: "zip" };
  }
  if (city && state) {
    const hit = latLngForCityState(city, state);
    if (hit) return { ...hit, resolution: "city" };
  }
  // State-only: average all ZIP centroids for that state to get a rough center
  if (state) {
    const code = normalizeState(state);
    if (zipByCode) {
      let sumLat = 0, sumLng = 0, n = 0;
      for (const z of Object.values(zipByCode)) {
        if (z.state === code) { sumLat += z.lat; sumLng += z.lng; n++; }
      }
      if (n > 0) return { lat: sumLat/n, lng: sumLng/n, resolution: "state" };
    }
  }
  return null;
}

// Get a company's best-known location based on priority rules:
// 1. If company has ZIP -> ZIP centroid
// 2. Else if company has city + state -> city centroid
// 3. Else -> null (caller decides what to do; state-only goes in a separate bucket)
//
// `company` is expected to have `.properties.zip`, `.properties.city`,
// `.properties.state` from HubSpot. Accepts a few alternate property names too.
//
// IMPORTANT: Requires a valid US state code before trusting ZIP lookups. This
// prevents foreign companies whose postal codes happen to collide with US ZIPs
// (e.g. Turkish ZIP "38080" matching US ZIP "38080" in Tennessee) from being
// incorrectly placed inside US radius searches.
export function locationForCompany(company) {
  if (!company || !company.properties) return null;
  const p = company.properties;
  const zip = p.zip || p.postal_code || p.zipcode || "";
  const city = (p.city || "").trim();
  const state = normalizeState(p.state || "");

  // No valid US state -> this is either US data missing state, or a foreign
  // company. Either way we can't trust the ZIP or city alone to place it
  // safely on a US map. Reject.
  if (!state) return null;

  if (zip) {
    const hit = latLngForZip(zip);
    if (hit) {
      // Final sanity check: the ZIP's resolved state should match the
      // company's stated state. If not, the ZIP is probably wrong (foreign
      // company with colliding postal code, or typo). Use city instead.
      const zipState = hit && (zipByCode[String(zip).replace(/[^\d]/g, "").slice(0,5).padStart(5,"0")] || {}).state;
      if (zipState && zipState === state) {
        return { ...hit, basis: "zip" };
      }
      // Fall through to city lookup -- don't trust the ZIP
    }
  }
  if (city) {
    const hit = latLngForCityState(city, state);
    if (hit) return { ...hit, basis: "city" };
  }
  // State-only is handled by the caller via stateForCompany() below
  return null;
}

// What state does this company belong to? Returns 2-letter code or "".
// Used for the "state-only fallback" bucket.
export function stateForCompany(company) {
  if (!company || !company.properties) return "";
  return normalizeState(company.properties.state || "");
}
